import React from 'react';
import GiftedSolutionsLogo from './GiftedSolutionsLogo';

const LogoTest = () => {
  return (
    <div className="p-8 space-y-12 bg-gray-50 min-h-screen">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">Gifted Solutions</h1>
        <p className="text-xl text-gray-600 mb-8">Logo Implementation</p>

        {/* Existing Logo */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Current Logo</h2>
          <div className="flex justify-center">
            <img
              src="/images/logo/logo.jpg"
              alt="Gifted Solutions Logo"
              className="h-24 w-auto rounded-lg shadow-lg"
            />
          </div>
        </div>

        {/* Custom Component Logo */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Custom Component Version</h2>
          <div className="flex justify-center">
            <GiftedSolutionsLogo size="xlarge" showTagline={true} />
          </div>
        </div>
      </div>
      
      {/* Different sizes */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Different Sizes</h2>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">Small:</span>
            <GiftedSolutionsLogo size="small" showTagline={true} />
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">Medium:</span>
            <GiftedSolutionsLogo size="medium" showTagline={true} />
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">Large:</span>
            <GiftedSolutionsLogo size="large" showTagline={true} />
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">XLarge:</span>
            <GiftedSolutionsLogo size="xlarge" showTagline={true} />
          </div>
        </div>
      </div>

      {/* Different variants */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Different Variants</h2>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">Default:</span>
            <GiftedSolutionsLogo size="medium" variant="default" />
          </div>
          
          <div className="flex items-center space-x-4 bg-gray-800 p-4 rounded">
            <span className="w-20 text-sm text-white">White:</span>
            <GiftedSolutionsLogo size="medium" variant="white" />
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">Minimal:</span>
            <GiftedSolutionsLogo size="medium" variant="minimal" />
          </div>
        </div>
      </div>

      {/* With and without tagline */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">With/Without Tagline</h2>

        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">With:</span>
            <GiftedSolutionsLogo size="medium" showTagline={true} />
          </div>

          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm">Without:</span>
            <GiftedSolutionsLogo size="medium" showTagline={false} />
          </div>
        </div>
      </div>

      {/* Current Logo Usage Examples */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Current Logo Usage Examples</h2>

        {/* Header Example with existing logo */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Header Navigation (Current Implementation)</h3>
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img
                src="/images/logo/logo.jpg"
                alt="Gifted Solutions"
                className="h-10 w-auto rounded-lg"
              />
              <span className="text-lg font-bold text-primary-600">Gifted Solutions</span>
            </div>
            <div className="flex space-x-4 text-sm text-gray-600">
              <span>Home</span>
              <span>Shop</span>
              <span>About</span>
              <span>Contact</span>
            </div>
          </div>
        </div>

        {/* Footer Example with existing logo */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Footer (Current Implementation)</h3>
          <div className="flex items-center space-x-3">
            <img
              src="/images/logo/logo.jpg"
              alt="Gifted Solutions"
              className="h-8 w-auto rounded-lg"
            />
            <div>
              <span className="text-lg font-bold text-white">Gifted Solutions</span>
              <p className="text-xs text-gray-300 uppercase tracking-wide">Branding & Marketing</p>
            </div>
          </div>
        </div>

        {/* Different sizes of existing logo */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Logo Sizes</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <span className="w-20 text-sm">Small:</span>
              <img src="/images/logo/logo.jpg" alt="Logo" className="h-6 w-auto rounded" />
            </div>
            <div className="flex items-center space-x-4">
              <span className="w-20 text-sm">Medium:</span>
              <img src="/images/logo/logo.jpg" alt="Logo" className="h-10 w-auto rounded-lg" />
            </div>
            <div className="flex items-center space-x-4">
              <span className="w-20 text-sm">Large:</span>
              <img src="/images/logo/logo.jpg" alt="Logo" className="h-16 w-auto rounded-lg" />
            </div>
            <div className="flex items-center space-x-4">
              <span className="w-20 text-sm">XLarge:</span>
              <img src="/images/logo/logo.jpg" alt="Logo" className="h-24 w-auto rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Custom Component Usage Examples */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Custom Component Usage Examples</h2>

        {/* Header Example */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <h3 className="text-sm font-medium text-gray-600 mb-3">Header Navigation (Component Version)</h3>
          <div className="flex justify-between items-center">
            <GiftedSolutionsLogo size="medium" showTagline={false} />
            <div className="flex space-x-4 text-sm text-gray-600">
              <span>Home</span>
              <span>Shop</span>
              <span>About</span>
              <span>Contact</span>
            </div>
          </div>
        </div>

        {/* Footer Example */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Footer (Component Version)</h3>
          <GiftedSolutionsLogo size="small" showTagline={true} variant="white" />
        </div>
      </div>

      {/* Available Logo Files */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Available Logo Files</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-medium mb-2">Current Logo (logo.jpg)</h3>
            <img src="/images/logo/logo.jpg" alt="Current Logo" className="h-12 w-auto mb-2 rounded" />
            <p className="text-sm text-gray-600">Your existing brand logo - now implemented</p>
            <p className="text-xs text-gray-500 mt-1">Path: /images/logo/logo.jpg</p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-medium mb-2">Icon Only (logo.svg)</h3>
            <img src="/logo.svg" alt="Logo Icon" className="h-12 w-12 mb-2" />
            <p className="text-sm text-gray-600">Custom SVG for favicons and small spaces</p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-medium mb-2">Logo with Text (logo-text.svg)</h3>
            <img src="/logo-text.svg" alt="Logo with Text" className="h-12 mb-2" />
            <p className="text-sm text-gray-600">Custom SVG with text for headers</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoTest;
